import { Trans, useTranslation } from 'react-i18next';
import { Box, Card, Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RailzButton } from '@railzai/railz-uikit-react';

import { useHistory } from 'react-router';

import storeService from '../../store';

import useStyle from './style';

import { Header } from 'components';
import { generateDropdown, showSnackbar } from 'helpers/common.helper';
import { CustomerState } from 'store/features/account/customer/customer.state';
import { getCustomerState } from 'store/features/account/customer/customer.selector';
import { AutoDeletePeriods } from 'types';
import { getProfileState } from 'store/features/account/profile/profile.selector';
import { ProfileState } from 'store/features/account/profile/profile.state';
import {
  resetProfileResponse,
  updateTeamSettingsApi,
} from 'store/features/account/profile/profile.action';

const autoDeletePeriodDropdown = generateDropdown(AutoDeletePeriods);

interface Props {
  testid?: string;
}

export const Settings: React.FC<Props> = ({
  testid = 'test-header-settings-page',
}): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const store = storeService.getStore();
  const customer = useSelector<ReturnType<typeof store.getState>, CustomerState>(getCustomerState);
  const profile = useSelector<ReturnType<typeof store.getState>, ProfileState>(getProfileState);
  const classes = useStyle();
  const dispatch = useDispatch();
  const [autoDeletePeriod, setAutoDeletePeriod] = useState(
    profile?.teamSettings?.autoDeleteBusinessPeriod || AutoDeletePeriods.NEVER,
  );

  const onPeriodChange = (event): void => {
    setAutoDeletePeriod(event.target.value);
  };

  const submitTeamSettings = (): void => {
    dispatch(updateTeamSettingsApi({ autoDeleteBusinessPeriod: autoDeletePeriod }));
  };

  useEffect(() => {
    if (!customer.companyName && !customer.loading) {
      history.push('/');
    }
  }, [customer]);

  useEffect(() => {
    setAutoDeletePeriod(profile?.teamSettings?.autoDeleteBusinessPeriod);
  }, [profile?.teamSettings]);

  useEffect(() => {
    if (profile.error) {
      if (profile?.error?.type === 'teamSettingsUpdate') {
        showSnackbar({ message: t('DASHBOARD_SAVE_TOAST_ERROR'), type: 'error' });
        setAutoDeletePeriod(profile?.teamSettings?.autoDeleteBusinessPeriod);
      } else {
        showSnackbar({ message: t('DASHBOARD_SIGNUP_GENERIC_ERROR'), type: 'error' });
      }
    }
  }, [profile.loading]);

  useEffect(() => {
    if (profile?.response) {
      showSnackbar({
        message: t('DASHBOARD_SAVE_TOAST_SUCCESS'),
        type: 'success',
      });
    }
    dispatch(resetProfileResponse());
  }, [profile?.response]);

  return (
    <>
      <Header
        drawerMenu={true}
        title={t('DASHBOARD_NAV_SETTINGS')}
        leftComponent={null}
        rightComponent={null}
        testId={testid}
      />

      <Card className={classes.container}>
        <Typography className={classes.mainTitle}>
          {t('DASHBOARD_BUSINESS_SETTINGS_DESCRIPTION_HEADER')}
        </Typography>
        <Typography>{t('DASHBOARD_BUSINESS_SETTINGS_DESCRIPTION_SUBHEADER')}</Typography>
        <Box className={classes.containerAction}>
          <Grid className={classes.gridContainer}>
            <Typography className={classes.mainTitle}>
              {t('DASHBOARD_AUTO_DELETE_SETTINGS_HEADER')}
            </Typography>
          </Grid>
          <Grid className={classes.containerGrid}>
            <Typography className={classes.description}>
              <Trans
                i18nKey="DASHBOARD_AUTO_DELETE_SETTINGS_SUBHEADER"
                components={{ b: <strong /> }}
              />
            </Typography>
          </Grid>
          <TextField
            label={t('DASHBOARD_SETTINGS_AUTO_DELETE_PERIOD_LABEL')}
            margin="dense"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              'data-testid': 'auto-delete-period-dropdown',
            }}
            SelectProps={{
              MenuProps: {
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                PopoverClasses: {
                  paper: classes.menu,
                },
              },
            }}
            select
            value={autoDeletePeriod}
            onChange={onPeriodChange}
            classes={{
              root: classes.autoDeleteSelect,
            }}
            data-testid="auto-delete-period-input"
          >
            {autoDeletePeriodDropdown.map((period, index) => (
              <MenuItem className={classes.menuItem} key={index} value={period.value}>
                {period?.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Card>
      {autoDeletePeriod !== profile?.teamSettings?.autoDeleteBusinessPeriod && (
        <div className={classes.buttonContainer}>
          <RailzButton
            size="medium"
            onClick={submitTeamSettings}
            data-testid="submit-auto-delete-period"
            label={t('DASHBOARD_BRAND_SAVE_CTA')}
            className={classes.saveCta}
          />
          <RailzButton
            size="small"
            onClick={(): void =>
              setAutoDeletePeriod(profile?.teamSettings?.autoDeleteBusinessPeriod)
            }
            data-testid="cancel-auto-delete-period"
            type="reset"
            label={t('DASHBOARD_CANCEL_CTA')}
            className={classes.discardCta}
          />
        </div>
      )}
    </>
  );
};
